import React from 'react'
import { Modal, Form, Input, Button } from 'antd'
import { LockOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { FormInstance } from 'antd/es/form'
import { isPasswordPolicyValid } from '../../utils/passwordPolicy'

interface PasswordModalProps {
    visible: boolean
    loading: boolean
    form: FormInstance
    onCancel: () => void
    onSubmit: (values: {
        current_password: string
        new_password: string
    }) => Promise<void>
}

export const PasswordModal: React.FC<PasswordModalProps> = ({
    visible,
    loading,
    form,
    onCancel,
    onSubmit,
}) => {
    const { t } = useTranslation('profile')
    
    return (
        <Modal
            title={t('security.changePassword')}
            open={visible}
            onCancel={onCancel}
            footer={null}
            maskClosable={false}
        >
            <Form form={form} layout="vertical" onFinish={onSubmit}>
                <Form.Item
                    name="current_password"
                    rules={[{ required: true, message: t('security.currentPasswordRequired') }]}
                >
                    <Input.Password
                        prefix={<LockOutlined />}
                        placeholder={t('security.currentPassword')}
                    />
                </Form.Item>

                <Form.Item
                    name="new_password"
                    dependencies={['current_password']}
                    rules={[
                        { required: true, message: t('security.newPasswordRequired') },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || value !== getFieldValue('current_password')) {
                                    return Promise.resolve()
                                }
                                return Promise.reject(new Error(t('security.passwordReuse')))
                            },
                        }),
                        {
                            validator(_, value) {
                                if (!value || isPasswordPolicyValid(value)) {
                                    return Promise.resolve()
                                }
                                return Promise.reject(new Error(t('security.passwordPolicy')))
                            },
                        },
                    ]}
                >
                    <Input.Password
                        prefix={<LockOutlined />}
                        placeholder={t('security.newPassword')}
                    />
                </Form.Item>

                <Form.Item
                    name="confirm_password"
                    dependencies={['new_password']}
                    rules={[
                        { required: true, message: t('security.confirmPasswordRequired') },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (
                                    !value ||
                                    getFieldValue('new_password') === value
                                ) {
                                    return Promise.resolve()
                                }
                                return Promise.reject(
                                    new Error(t('security.passwordMismatch'))
                                )
                            },
                        }),
                    ]}
                >
                    <Input.Password
                        prefix={<LockOutlined />}
                        placeholder={t('security.confirmNewPassword')}
                    />
                </Form.Item>

                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading} block>
                        {t('security.confirmButton')}
                    </Button>
                </Form.Item>
            </Form>
        </Modal>
    )
}
